import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LandingNav from '../components/shared/LandingNav';
import SiteFooter from '../components/shared/SiteFooter';
import Seo from '../components/Seo';
import BlogGenerateControls from '../components/blog/BlogGenerateControls';
import { useTheme } from '../contexts/ThemeContext';
import { AuthContext } from '../contexts/AuthContext';
import { fetchBlogPosts, generateBlogPost } from '../services/blogApi';
import { fetchBlogIndexSnapshot } from '../services/staticContent';
import { isPrerendering } from '../utils/prerendering';
import '../components/blog/Blog.css';

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

function statusLabel(status) {
  if (status === 'published') return 'Published';
  if (status === 'rejected') return 'Hidden';
  return 'In Review';
}

function statusPillClass(status) {
  if (status === 'published') return 'blog-status-pill--published';
  if (status === 'rejected') return 'blog-status-pill--hidden';
  return 'blog-status-pill--in-review';
}

function BlogPostCard({ post, to, text, muted, showStatus }) {
  return (
    <Link to={to} className="blog-grid-card" style={{ color: text }}>
      <div className="blog-grid-cover">
        {post.cover_image_url ? (
          <img src={post.cover_image_url} alt="" />
        ) : (
          <div className="blog-grid-cover-empty" aria-hidden />
        )}
        {showStatus && (
          <span className={`blog-status-pill ${statusPillClass(post.status)} blog-grid-status`}>
            {statusLabel(post.status)}
          </span>
        )}
      </div>
      <h2 className="blog-grid-title">{post.title}</h2>
      {post.meta_description && (
        <p className="blog-grid-dek" style={{ color: muted }}>
          {post.meta_description}
        </p>
      )}
      <p className="blog-grid-date" style={{ color: muted }}>
        {formatDate(post.published_at || post.created_at)}
      </p>
    </Link>
  );
}

const BlogIndex = () => {
  const { colors } = useTheme();
  const { user } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const isSuperuser = user?.superuser === true;
  const generateRef = useRef(null);

  const [posts, setPosts] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Snapshot first so the prerendered HTML lists real posts for crawlers.
      const snapshot = await fetchBlogIndexSnapshot();
      if (cancelled) return;
      const snapshotPosts = snapshot?.posts || [];
      if (snapshotPosts.length) {
        setPosts(snapshotPosts);
        setError(null);
        setLoading(false);
      }

      if (isPrerendering() || !process.env.REACT_APP_BACKEND_URL) {
        setLoading(false);
        return;
      }

      try {
        if (!snapshotPosts.length) {
          setLoading(true);
          setError(null);
        }
        const published = await fetchBlogPosts({ token: user?.token });
        if (cancelled) return;
        setPosts(published.posts || []);
        setError(null);

        if (isSuperuser && user?.token) {
          const drafts = await fetchBlogPosts({
            token: user.token,
            status: 'pending_review',
          });
          if (!cancelled) setPending(drafts.posts || []);
        } else {
          setPending([]);
        }
      } catch (err) {
        // Stale snapshot content beats an error message.
        if (!cancelled && !snapshotPosts.length) setError(err.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.token, isSuperuser]);

  useEffect(() => {
    if (!showGenerateModal) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !generating) setShowGenerateModal(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [showGenerateModal, generating]);

  const handleGenerate = async () => {
    if (!user?.token || generating) return;
    const payload = generateRef.current?.getPayload?.() || { valid: true };
    if (!payload.valid) {
      setGenError(payload.error || 'Invalid direction');
      return;
    }
    setGenerating(true);
    setGenError(null);
    try {
      const data = await generateBlogPost(user.token, {
        topic: payload.topic,
        direction: payload.direction,
      });
      if (!data.jobId) throw new Error('No job id returned');
      navigate(`/blog/generating/${data.jobId}`);
    } catch (err) {
      setGenError(err.message || 'Generation failed');
      setGenerating(false);
    }
  };

  const openGenerateModal = () => {
    setGenError(null);
    setShowGenerateModal(true);
  };

  const text = colors.text;
  const muted = colors.textMuted || colors.textLight || '#94a3b8';
  const primary = colors.primary || '#3b82f6';

  return (
      <div className="blog-page blog-page--index" style={{ backgroundColor: colors.bgPrimary, color: text }}>
      <Seo
        title="Blog | PrecisionTrader"
        description="Process-focused writing on trading discipline, rule enforcement, and execution."
        canonicalPath="/blog"
      />
      <div className="blog-index-ambient" aria-hidden>
        <div className="blog-index-ambient-glow" />
        <div className="blog-index-ambient-grid" />
      </div>
      <LandingNav />
      <header className="blog-index-hero">
        <div className="blog-index-hero-inner">
          {isSuperuser && (
            <button
              type="button"
              className="blog-index-generate-btn"
              onClick={openGenerateModal}
              style={{
                background: primary,
                borderColor: primary,
              }}
            >
              Generate new post
            </button>
          )}
          <div className="blog-index-hero-copy">
            <h1 className="blog-index-h1">Blog</h1>
            <p className="blog-index-sub" style={{ color: muted }}>
              Short pieces on sticking to your rules—not chasing outcomes.
            </p>
          </div>
          <a
            href="#blog-posts"
            className="blog-index-scroll-hint"
            style={{ color: muted }}
            aria-label="Scroll to posts"
          >
            <span aria-hidden>↓</span>
          </a>
        </div>
      </header>

      <div className="blog-index-col">
        <div id="blog-posts" className="blog-index-posts">
        {isSuperuser && pending.length > 0 && (
          <section className="blog-index-section">
            <p className="blog-index-section-label" style={{ color: muted }}>
              In review
            </p>
            <div className="blog-post-grid">
              {pending.map((post) => (
                <BlogPostCard
                  key={post.id}
                  post={post}
                  to={`/blog/edit/${post.id}`}
                  text={text}
                  muted={muted}
                  showStatus
                />
              ))}
            </div>
          </section>
        )}

        {loading && <p style={{ color: muted }}>Loading…</p>}
        {error && <p style={{ color: colors.danger || '#f87171' }}>{error}</p>}
        {!loading && !error && posts.length === 0 && pending.length === 0 && (
          <p style={{ color: muted }}>No posts yet.</p>
        )}
        {!loading && posts.length > 0 && (
          <section className="blog-index-section">
            {isSuperuser && pending.length > 0 && (
              <p className="blog-index-section-label" style={{ color: muted }}>
                Published
              </p>
            )}
            <div className="blog-post-grid">
              {posts.map((post) => (
                <BlogPostCard
                  key={post.id}
                  post={post}
                  to={`/blog/${post.slug}`}
                  text={text}
                  muted={muted}
                />
              ))}
            </div>
          </section>
        )}
        </div>
      </div>
      <SiteFooter />

      {showGenerateModal && (
        <div
          className="blog-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!generating) setShowGenerateModal(false);
          }}
        >
          <div
            className="blog-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="blog-generate-modal-title"
            style={{ backgroundColor: colors.bgPrimary || '#0f172a', color: text }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="blog-modal-header">
              <h2 id="blog-generate-modal-title">Generate new post</h2>
              <button
                type="button"
                className="blog-modal-close"
                aria-label="Close"
                disabled={generating}
                onClick={() => setShowGenerateModal(false)}
                style={{ color: muted }}
              >
                ×
              </button>
            </div>
            <BlogGenerateControls ref={generateRef} token={user?.token} compact />
            {genError && (
              <p style={{ margin: '0.75rem 0 0', color: colors.danger || '#f87171', fontSize: '0.9rem' }}>
                {genError}
              </p>
            )}
            <div className="blog-modal-actions">
              <button
                type="button"
                className="blog-modal-btn-secondary"
                disabled={generating}
                onClick={() => setShowGenerateModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="blog-modal-btn-primary"
                disabled={generating}
                onClick={handleGenerate}
                style={{
                  background: generating ? 'transparent' : primary,
                  color: generating ? muted : '#fff',
                  borderColor: primary,
                }}
              >
                {generating ? 'Starting…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogIndex;
